import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaPlay, FaChalkboardTeacher, FaCalendarAlt, FaLock } from 'react-icons/fa';
import { supabase } from '../services/supabaseClient';

const extractVideoId = (url) => {
    if (!url) return null;
    const patterns = [
        /youtu\.be\/([^?&\s]+)/,
        /youtube\.com\/watch\?v=([^?&\s]+)/,
        /youtube\.com\/embed\/([^?&\s]+)/,
        /youtube\.com\/shorts\/([^?&\s]+)/
    ];
    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
    }
    if (/^[a-zA-Z0-9_-]{11}$/.test(url.trim())) return url.trim();
    return null;
};

const embedUrl = (id) =>
    `https://www.youtube.com/embed/${id}?autoplay=1&modestbranding=1&rel=0&iv_load_policy=3&cc_load_policy=0&color=white`;

const thumbUrl = (id) =>
    `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;

// ── Card de taller ──────────────────────────────────────────────────────────
const TallerCard = ({ taller, index, isAdmin, onPlay, onEdit, onDelete }) => {
    const [hovered, setHovered] = useState(false);
    const videoId = extractVideoId(taller.youtube_url);

    return (
        <div
            style={{
                background: '#fff',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: hovered
                    ? '0 12px 40px rgba(0,0,0,0.15)'
                    : '0 2px 12px rgba(0,0,0,0.07)',
                border: '1px solid #e8eaf0',
                transition: 'box-shadow 0.25s, transform 0.25s',
                transform: hovered ? 'translateY(-4px)' : 'none',
                display: 'flex',
                flexDirection: 'column'
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {/* Thumbnail */}
            <div
                onClick={() => videoId && onPlay(taller)}
                style={{
                    position: 'relative',
                    paddingBottom: '56.25%',
                    background: '#0f172a',
                    cursor: videoId ? 'pointer' : 'default',
                    overflow: 'hidden'
                }}
            >
                {videoId ? (
                    <>
                        <img
                            src={thumbUrl(videoId)}
                            alt={taller.title}
                            onError={(e) => { e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }}
                            style={{
                                position: 'absolute', inset: 0,
                                width: '100%', height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.4s',
                                transform: hovered ? 'scale(1.04)' : 'scale(1)'
                            }}
                        />
                        {/* Gradient overlay */}
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 60%)',
                            transition: 'opacity 0.25s',
                            opacity: hovered ? 1 : 0.6
                        }} />
                        {/* Badge número */}
                        <div style={{
                            position: 'absolute', top: '12px', left: '12px',
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            color: '#fff', borderRadius: '8px',
                            padding: '4px 10px', fontSize: '0.72rem', fontWeight: 700,
                            letterSpacing: '0.5px'
                        }}>
                            Taller #{String(index + 1).padStart(2, '0')}
                        </div>
                        {/* Play button */}
                        <div style={{
                            position: 'absolute', top: '50%', left: '50%',
                            transform: `translate(-50%, -50%) scale(${hovered ? 1.1 : 1})`,
                            width: '56px', height: '56px',
                            background: 'rgba(255,255,255,0.95)',
                            borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                            transition: 'transform 0.25s'
                        }}>
                            <FaPlay size={18} color="#667eea" style={{ marginLeft: '3px' }} />
                        </div>
                        {/* Duración / fecha abajo */}
                        {taller.fecha && (
                            <div style={{
                                position: 'absolute', bottom: '10px', right: '12px',
                                background: 'rgba(0,0,0,0.65)',
                                backdropFilter: 'blur(4px)',
                                color: '#fff', borderRadius: '6px',
                                padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600,
                                display: 'flex', alignItems: 'center', gap: '5px'
                            }}>
                                <FaCalendarAlt size={10} /> {taller.fecha}
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{
                        position: 'absolute', inset: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#1e293b'
                    }}>
                        <span style={{ color: '#475569', fontSize: '0.85rem' }}>Video no disponible</span>
                    </div>
                )}
            </div>

            {/* Info */}
            <div style={{ padding: '16px 18px 18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{
                    fontSize: '0.98rem', fontWeight: 700,
                    color: '#0f172a', margin: '0 0 8px',
                    lineHeight: 1.35
                }}>
                    {taller.title}
                </h3>
                {taller.description && (
                    <p style={{
                        fontSize: '0.84rem', color: '#64748b',
                        margin: '0', lineHeight: 1.55,
                        flex: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        {taller.description}
                    </p>
                )}

                {/* Botón ver */}
                {videoId && (
                    <button
                        onClick={() => onPlay(taller)}
                        style={{
                            marginTop: '14px',
                            width: '100%',
                            padding: '9px',
                            background: hovered
                                ? 'linear-gradient(135deg, #667eea, #764ba2)'
                                : '#f1f5f9',
                            color: hovered ? '#fff' : '#667eea',
                            border: 'none',
                            borderRadius: '10px',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            transition: 'background 0.25s, color 0.25s',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px'
                        }}
                    >
                        <FaPlay size={11} /> Ver taller
                    </button>
                )}

                {/* Admin actions */}
                {isAdmin && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(taller); }}
                            style={{
                                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                gap: '5px', background: '#eff6ff', color: '#3b82f6',
                                border: 'none', borderRadius: '8px', padding: '7px',
                                fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer'
                            }}
                        >
                            <FaEdit size={11} /> Editar
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onDelete(taller); }}
                            style={{
                                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                gap: '5px', background: '#fff1f2', color: '#ef4444',
                                border: 'none', borderRadius: '8px', padding: '7px',
                                fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer'
                            }}
                        >
                            <FaTrash size={11} /> Eliminar
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ── Componente principal ────────────────────────────────────────────────────
const Talleres = ({ user }) => {
    const [talleres, setTalleres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal video
    const [playingTaller, setPlayingTaller] = useState(null);

    // Modal formulario
    const [showModal, setShowModal] = useState(false);
    const [editingTaller, setEditingTaller] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Modal eliminar
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [tallerToDelete, setTallerToDelete] = useState(null);

    const isAdmin = user?.plan === 'ADMI';

    const [formData, setFormData] = useState({ title: '', description: '', youtube_url: '', fecha: '' });
    const [formErrors, setFormErrors] = useState({});

    const loadTalleres = async () => {
        try {
            setLoading(true);
            setError(null);
            const { data, error: dbError } = await supabase
                .from('talleres')
                .select('*')
                .order('created_at', { ascending: false });
            if (dbError) throw dbError;
            setTalleres(data || []);
        } catch (err) {
            console.error('Error cargando talleres:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadTalleres(); }, []);

    // Cerrar modal de video con Escape
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') setPlayingTaller(null); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const resetForm = () => {
        setFormData({ title: '', description: '', youtube_url: '', fecha: '' });
        setFormErrors({});
        setEditingTaller(null);
    };

    const openAdd = () => { resetForm(); setShowModal(true); };

    const openEdit = (taller) => {
        setEditingTaller(taller);
        setFormData({
            title: taller.title || '',
            description: taller.description || '',
            youtube_url: taller.youtube_url || '',
            fecha: taller.fecha || ''
        });
        setFormErrors({});
        setShowModal(true);
    };

    const validate = () => {
        const errors = {};
        if (!formData.title.trim()) errors.title = 'El título es obligatorio';
        if (!formData.youtube_url.trim()) {
            errors.youtube_url = 'La URL del video es obligatoria';
        } else if (!extractVideoId(formData.youtube_url)) {
            errors.youtube_url = 'URL de video no válida';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                youtube_url: formData.youtube_url.trim(),
                fecha: formData.fecha || null
            };
            if (editingTaller) {
                const { error: err } = await supabase.from('talleres').update(payload).eq('id', editingTaller.id);
                if (err) throw err;
            } else {
                const { error: err } = await supabase.from('talleres').insert([payload]);
                if (err) throw err;
            }
            setShowModal(false);
            resetForm();
            loadTalleres();
        } catch (err) {
            console.error('Error guardando:', err);
            setFormErrors({ general: 'Error al guardar. Inténtalo de nuevo.' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!tallerToDelete) return;
        try {
            const { error: err } = await supabase.from('talleres').delete().eq('id', tallerToDelete.id);
            if (err) throw err;
            setShowDeleteModal(false);
            setTallerToDelete(null);
            loadTalleres();
        } catch (err) {
            console.error('Error eliminando:', err);
        }
    };

    const previewId = extractVideoId(formData.youtube_url);

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc' }}>

            {/* ── Header banner ─────────────────────────────────────────── */}
            <div style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 60%, #1e293b 100%)',
                padding: '36px 32px 32px',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Decoración fondo */}
                <div style={{
                    position: 'absolute', top: '-40px', right: '-40px',
                    width: '220px', height: '220px',
                    background: 'radial-gradient(circle, rgba(102,126,234,0.18) 0%, transparent 70%)',
                    borderRadius: '50%'
                }} />
                <div style={{
                    position: 'absolute', bottom: '-30px', left: '20%',
                    width: '160px', height: '160px',
                    background: 'radial-gradient(circle, rgba(118,75,162,0.15) 0%, transparent 70%)',
                    borderRadius: '50%'
                }} />

                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '16px', position: 'relative' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <div style={{
                                width: '44px', height: '44px',
                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                borderRadius: '12px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 4px 14px rgba(102,126,234,0.4)'
                            }}>
                                <FaChalkboardTeacher size={20} color="#fff" />
                            </div>
                            <h1 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: 800, margin: 0, letterSpacing: '-0.3px' }}>
                                Talleres
                            </h1>
                        </div>
                        <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.95rem' }}>
                            Capacitaciones y talleres especializados · Contenido exclusivo
                        </p>
                        {talleres.length > 0 && (
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                background: 'rgba(102,126,234,0.2)', border: '1px solid rgba(102,126,234,0.35)',
                                color: '#a5b4fc', borderRadius: '20px',
                                padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600,
                                marginTop: '10px'
                            }}>
                                <FaLock size={9} /> {talleres.length} {talleres.length === 1 ? 'taller disponible' : 'talleres disponibles'}
                            </div>
                        )}
                    </div>

                    {isAdmin && (
                        <button
                            onClick={openAdd}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                color: '#fff', border: 'none', borderRadius: '12px',
                                padding: '11px 22px', fontWeight: 700, cursor: 'pointer',
                                fontSize: '0.9rem',
                                boxShadow: '0 4px 16px rgba(102,126,234,0.45)',
                                transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                            <FaPlus size={13} /> Agregar Taller
                        </button>
                    )}
                </div>
            </div>

            {/* ── Contenido ─────────────────────────────────────────────── */}
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>

                {/* Error SQL hint (solo admin) */}
                {isAdmin && error && (
                    <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px' }}>
                        <strong>Tabla no encontrada.</strong> Ejecuta este SQL en Supabase:
                        <pre style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', marginTop: '10px', fontSize: '0.78rem', overflowX: 'auto', margin: '10px 0 0' }}>{`CREATE TABLE talleres (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  title text NOT NULL,
  description text,
  youtube_url text NOT NULL,
  fecha text,
  created_at timestamp with time zone DEFAULT now()
);
ALTER TABLE talleres DISABLE ROW LEVEL SECURITY;`}</pre>
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div style={{ textAlign: 'center', padding: '80px 0' }}>
                        <div className="spinner-border" style={{ color: '#667eea', width: '2.5rem', height: '2.5rem' }} role="status" />
                        <p style={{ color: '#94a3b8', marginTop: '16px', fontWeight: 500 }}>Cargando talleres...</p>
                    </div>
                )}

                {/* Empty state */}
                {!loading && !error && talleres.length === 0 && (
                    <div style={{
                        textAlign: 'center', padding: '80px 20px',
                        background: '#fff', borderRadius: '20px',
                        border: '2px dashed #e2e8f0',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
                    }}>
                        <div style={{
                            width: '72px', height: '72px', margin: '0 auto 20px',
                            background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                            borderRadius: '20px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <FaChalkboardTeacher size={32} color="#94a3b8" />
                        </div>
                        <h3 style={{ color: '#334155', fontWeight: 700, fontSize: '1.1rem', marginBottom: '8px' }}>
                            No hay talleres disponibles aún
                        </h3>
                        <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.9rem' }}>
                            {isAdmin ? 'Haz clic en "Agregar Taller" para publicar el primer taller.' : 'Pronto habrá contenido disponible.'}
                        </p>
                    </div>
                )}

                {/* Grid */}
                {!loading && talleres.length > 0 && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: '24px'
                    }}>
                        {talleres.map((taller, idx) => (
                            <TallerCard
                                key={taller.id}
                                taller={taller}
                                index={idx}
                                isAdmin={isAdmin}
                                onPlay={setPlayingTaller}
                                onEdit={openEdit}
                                onDelete={(t) => { setTallerToDelete(t); setShowDeleteModal(true); }}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* ── Modal Reproductor ─────────────────────────────────────── */}
            {playingTaller && (() => {
                const vid = extractVideoId(playingTaller.youtube_url);
                return (
                    <div
                        onClick={() => setPlayingTaller(null)}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 2000,
                            background: 'rgba(0,0,0,0.92)',
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            padding: '20px',
                            backdropFilter: 'blur(6px)'
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: '100%', maxWidth: '900px' }}
                        >
                            {/* Cabecera del player */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                <div>
                                    <h2 style={{ color: '#fff', margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                                        {playingTaller.title}
                                    </h2>
                                    {playingTaller.fecha && (
                                        <span style={{ color: '#94a3b8', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
                                            <FaCalendarAlt size={10} /> {playingTaller.fecha}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => setPlayingTaller(null)}
                                    style={{
                                        background: 'rgba(255,255,255,0.1)',
                                        border: '1px solid rgba(255,255,255,0.15)',
                                        borderRadius: '10px', color: '#fff',
                                        width: '38px', height: '38px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', fontSize: '1rem'
                                    }}
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            {/* Iframe sin branding */}
                            <div style={{
                                position: 'relative', paddingBottom: '56.25%',
                                borderRadius: '16px', overflow: 'hidden',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                                background: '#000'
                            }}>
                                {vid ? (
                                    <iframe
                                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                                        src={embedUrl(vid)}
                                        title={playingTaller.title}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                        allowFullScreen
                                    />
                                ) : (
                                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <span style={{ color: '#475569' }}>Video no disponible</span>
                                    </div>
                                )}
                            </div>

                            {playingTaller.description && (
                                <p style={{ color: '#94a3b8', margin: '14px 0 0', fontSize: '0.88rem', lineHeight: 1.6 }}>
                                    {playingTaller.description}
                                </p>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* ── Modal Formulario ──────────────────────────────────────── */}
            {showModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
                    zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '20px', backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '20px',
                        width: '100%', maxWidth: '540px',
                        maxHeight: '92vh', overflowY: 'auto',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.25)'
                    }}>
                        {/* Header */}
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '20px 24px 16px',
                            borderBottom: '1px solid #f1f5f9'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>
                                {editingTaller ? 'Editar Taller' : 'Agregar Taller'}
                            </h2>
                            <button
                                onClick={() => { setShowModal(false); resetForm(); }}
                                style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}
                            >
                                <FaTimes size={14} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px' }}>
                            {formErrors.general && (
                                <div style={{ background: '#fff1f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', color: '#ef4444', fontSize: '0.85rem' }}>
                                    {formErrors.general}
                                </div>
                            )}

                            {/* Título */}
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '6px', fontSize: '0.85rem' }}>
                                    Título *
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="Ej: Taller de Contrataciones Públicas"
                                    style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${formErrors.title ? '#fca5a5' : '#e2e8f0'}`, borderRadius: '10px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                                    onFocus={e => e.target.style.borderColor = '#667eea'}
                                    onBlur={e => e.target.style.borderColor = formErrors.title ? '#fca5a5' : '#e2e8f0'}
                                />
                                {formErrors.title && <p style={{ color: '#ef4444', fontSize: '0.78rem', margin: '4px 0 0' }}>{formErrors.title}</p>}
                            </div>

                            {/* URL del video */}
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '6px', fontSize: '0.85rem' }}>
                                    URL del video *
                                </label>
                                <input
                                    type="text"
                                    value={formData.youtube_url}
                                    onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                                    placeholder="https://youtu.be/... o https://youtube.com/watch?v=..."
                                    style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${formErrors.youtube_url ? '#fca5a5' : '#e2e8f0'}`, borderRadius: '10px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                                    onFocus={e => e.target.style.borderColor = '#667eea'}
                                    onBlur={e => e.target.style.borderColor = formErrors.youtube_url ? '#fca5a5' : '#e2e8f0'}
                                />
                                {formErrors.youtube_url && <p style={{ color: '#ef4444', fontSize: '0.78rem', margin: '4px 0 0' }}>{formErrors.youtube_url}</p>}

                                {/* Preview */}
                                {previewId && (
                                    <div style={{ marginTop: '10px', borderRadius: '10px', overflow: 'hidden', position: 'relative', paddingBottom: '30%', background: '#000' }}>
                                        <img
                                            src={thumbUrl(previewId)}
                                            onError={(e) => { e.target.src = `https://img.youtube.com/vi/${previewId}/hqdefault.jpg`; }}
                                            alt="preview"
                                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }}
                                        />
                                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.4), transparent)' }} />
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'rgba(255,255,255,0.9)', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <FaPlay size={11} color="#667eea" style={{ marginLeft: '2px' }} />
                                        </div>
                                        <span style={{ position: 'absolute', bottom: '8px', left: '12px', color: '#fff', fontSize: '0.72rem', fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                                            Vista previa
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Fecha */}
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '6px', fontSize: '0.85rem' }}>
                                    Fecha (opcional)
                                </label>
                                <input
                                    type="text"
                                    value={formData.fecha}
                                    onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                                    placeholder="Ej: Mayo 2025"
                                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                                    onFocus={e => e.target.style.borderColor = '#667eea'}
                                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                />
                            </div>

                            {/* Descripción */}
                            <div style={{ marginBottom: '22px' }}>
                                <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '6px', fontSize: '0.85rem' }}>
                                    Descripción (opcional)
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Breve descripción del taller..."
                                    rows={3}
                                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '0.9rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                                    onFocus={e => e.target.style.borderColor = '#667eea'}
                                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    style={{ padding: '10px 20px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '7px',
                                        padding: '10px 22px',
                                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                        color: '#fff', border: 'none', borderRadius: '10px',
                                        fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
                                        fontSize: '0.88rem', opacity: submitting ? 0.7 : 1
                                    }}
                                >
                                    {submitting
                                        ? <><span className="spinner-border spinner-border-sm" /> Guardando...</>
                                        : <><FaSave size={13} /> {editingTaller ? 'Guardar cambios' : 'Agregar Taller'}</>
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Modal Confirmar Eliminar ───────────────────────────────── */}
            {showDeleteModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
                    zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '20px', backdropFilter: 'blur(4px)'
                }}>
                    <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '400px', padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                        <div style={{ width: '48px', height: '48px', background: '#fff1f2', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                            <FaTrash size={18} color="#ef4444" />
                        </div>
                        <h3 style={{ margin: '0 0 8px', color: '#0f172a', fontWeight: 700 }}>Eliminar taller</h3>
                        <p style={{ color: '#64748b', margin: '0 0 24px', fontSize: '0.9rem', lineHeight: 1.5 }}>
                            ¿Eliminar <strong>"{tallerToDelete?.title}"</strong>? Esta acción no se puede deshacer.
                        </p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowDeleteModal(false)} style={{ padding: '10px 18px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}>
                                Cancelar
                            </button>
                            <button onClick={handleDelete} style={{ padding: '10px 18px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' }}>
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Talleres;
